import type { Geo } from '../schemas/primitives.js';

const EARTH_RADIUS_KM = 6371;

/**
 * Great-circle distance in km (haversine). Accurate to ~0.5% — plenty for
 * "nearest first" sorting and display chips; not for navigation.
 */
export function haversineKm(a: Geo, b: Geo): number {
  const toRad = (deg: number): number => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(s));
}

/**
 * True when a point is safe to plot on a map. A finite, in-range coordinate
 * that is NOT the null island (0,0) — our sentinel for "address entered but
 * not yet geocoded". Plotting (0,0) would drop a pin in the Gulf of Guinea,
 * so the map surfaces expose these separately ("not yet on the map") rather
 * than silently mis-placing them.
 */
export function hasRealLocation(g: Geo | null | undefined): g is Geo {
  return (
    !!g &&
    Number.isFinite(g.lat) &&
    Number.isFinite(g.lng) &&
    g.lat >= -90 &&
    g.lat <= 90 &&
    g.lng >= -180 &&
    g.lng <= 180 &&
    !(g.lat === 0 && g.lng === 0)
  );
}

/**
 * Split a list into the items that CAN be plotted and those that cannot,
 * preserving input order in each bucket. Pure — the map UIs turn `placed`
 * into markers and list `unplaced` with a "add an address" nudge (no silent
 * drop). Generic over item shape via a `getGeo` accessor.
 */
export function partitionByLocation<T>(
  items: readonly T[],
  getGeo: (item: T) => Geo | null | undefined,
): { readonly placed: readonly T[]; readonly unplaced: readonly T[] } {
  const placed: T[] = [];
  const unplaced: T[] = [];
  for (const item of items) {
    if (hasRealLocation(getGeo(item))) placed.push(item);
    else unplaced.push(item);
  }
  return { placed, unplaced };
}
